import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Flame, Sparkles, Home as HomeIcon, Shield, Package, Wand2, Star } from "lucide-react";
import LionLogo from "../components/LionLogo";
import { api } from "../lib/api";

function FeaturedReview() {
  const [t, setT] = useState(null);
  const [count, setCount] = useState(0);
  useEffect(() => {
    api.get("/featured-review").then((r) => setT(r.data && r.data.id ? r.data : null));
    api.get("/testimonials").then((r) => setCount(r.data.length));
  }, []);
  if (!t) return null;
  const first = (t.name || "").split(" ")[0];
  return (
    <section className="py-20 px-6 md:px-10 border-y border-white/10 bg-[#0A0A0A]" data-testid="featured-review">
      <div className="max-w-4xl mx-auto text-center">
        <div className="overline mb-3 text-[#D4AF37]">Featured Review of the Month</div>
        <div className="flex justify-center gap-1 mb-6 text-[#D4AF37]">
          {Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} size={18} fill="#D4AF37" stroke="#D4AF37" />)}
        </div>
        <p className="font-serif text-2xl md:text-3xl italic leading-relaxed text-white/90">"{t.content}"</p>
        <div className="mt-8 overline">— {first}{t.location && ` · ${t.location}`}</div>
        {count > 0 && (
          <div className="mt-10 inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-white/50" data-testid="featured-review-count">
            <span className="h-px w-8 bg-[#D4AF37]/40" />
            Trusted by {count}+ happy {count === 1 ? "client" : "clients"}
            <span className="h-px w-8 bg-[#D4AF37]/40" />
          </div>
        )}
      </div>
    </section>
  );
}

const SERVICES = [
  { icon: Sparkles, title: "Tarot Readings", desc: "Clarity, direction, and insight through traditional tarot.", to: "/book-reading" },
  { icon: Flame, title: "Spiritual Consultations", desc: "One-on-one guidance for life's most important questions.", to: "/services" },
  { icon: HomeIcon, title: "House Cleansings", desc: "Reset your home's energy with sacred ritual.", to: "/house-calls" },
  { icon: Shield, title: "Protection Work", desc: "Custom rituals to shield home, family, and spirit.", to: "/services" },
  { icon: Package, title: "Spiritual Products", desc: "Hand-dressed candles, oils, baths, and protective tools.", to: "/shop" },
  { icon: Wand2, title: "Custom Services", desc: "Bespoke spiritual work tailored to your situation.", to: "/contact" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.pexels.com/photos/11573495/pexels-photo-11573495.jpeg')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="overline mb-6">Lyon's Den Protection · LLC</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#FFFFF0]">
              Spiritual Guidance,<br />
              <span className="text-[#D4AF37]">Protection</span>, and Empowerment<br />
              for Modern Life.
            </h1>
            <p className="mt-8 text-lg text-white/70 max-w-2xl leading-relaxed">
              Helping people gain clarity, confidence, protection, and direction through spiritual insight and practical guidance — rooted in decades of study across multiple traditions and the soil of Southern Louisiana.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/book-reading"
                className="bg-[#8B0000] hover:bg-[#A50000] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2"
                data-testid="hero-book-reading-btn"
              >
                Book a Reading <ArrowRight size={14} />
              </Link>
              <Link
                to="/shop"
                className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-8 py-4 text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2"
                data-testid="hero-shop-products-btn"
              >
                Shop Products
              </Link>
              <Link
                to="/services"
                className="text-white/80 hover:text-[#D4AF37] px-2 py-4 text-xs uppercase tracking-[0.2em] flex items-center gap-2"
                data-testid="hero-consultation-btn"
              >
                Schedule Consultation <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          <div className="md:col-span-5 flex justify-center">
            <div className="relative">
              <div className="absolute -inset-6 rounded-full bg-[#8B0000]/20 blur-3xl" />
              <LionLogo size={360} variant="full" className="relative drop-shadow-2xl" />
              <div className="mt-6 text-center">
                <div className="font-serif text-2xl italic text-[#D4AF37]">Shielded By Fire.</div>
                <div className="font-serif text-2xl italic text-[#D4AF37]">Guarded By Spirit.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED SERVICES */}
      <section className="py-20 md:py-28 px-6 md:px-10 max-w-7xl mx-auto" data-testid="featured-services">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <div className="overline mb-3">What We Offer</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight">
              Trusted spiritual services & products.
            </h2>
          </div>
          <Link to="/services" className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] flex items-center gap-2">
            View all services <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Link
                key={i}
                to={s.to}
                className="group bg-[#0A0A0A] border border-white/5 hover:border-[#D4AF37]/40 p-8 transition-all"
                data-testid={`featured-service-${i}`}
              >
                <Icon className="text-[#8B0000] group-hover:text-[#D4AF37] transition-colors" size={28} strokeWidth={1.4} />
                <h3 className="font-serif text-2xl mt-6">{s.title}</h3>
                <p className="text-white/60 mt-3 leading-relaxed">{s.desc}</p>
                <div className="mt-6 text-xs uppercase tracking-[0.2em] text-[#D4AF37] flex items-center gap-2">
                  Learn more <ArrowRight size={12} />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FOUNDER STRIP */}
      <section className="border-y border-white/10 bg-[#0A0A0A] py-20 md:py-28 px-6 md:px-10" data-testid="founder-strip">
        <div className="max-w-5xl mx-auto text-center">
          <div className="overline mb-4">The Founder</div>
          <h2 className="font-serif text-3xl md:text-5xl leading-tight">
            "Real protection begins with real understanding."
          </h2>
          <p className="mt-8 text-white/70 max-w-3xl mx-auto leading-relaxed text-lg">
            The Lion Chief was born and raised across rural Southern Louisiana. His path includes decades of study across Catholic, Christian, Apostolic, Islamic, and traditional spiritual systems — woven into a practice that is authentic, professional, and grounded.
          </p>
          <Link to="/about" className="inline-flex items-center gap-2 mt-10 text-xs uppercase tracking-[0.2em] text-[#D4AF37] hover:text-white">
            Read his story <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* FEATURED REVIEW */}
      <FeaturedReview />

      {/* CTA STRIP */}
      <section className="py-20 px-6 md:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link to="/book-reading" className="relative overflow-hidden group h-64 flex items-end p-8 border border-white/10" style={{ backgroundImage: "url('https://images.pexels.com/photos/36116207/pexels-photo-36116207.jpeg')", backgroundSize: "cover" }} data-testid="cta-book-reading">
            <div className="absolute inset-0 bg-black/60 group-hover:bg-black/40 transition-colors" />
            <div className="relative">
              <div className="overline text-[#D4AF37]">Tarot</div>
              <h3 className="font-serif text-3xl mt-2">Book a Reading</h3>
              <div className="mt-4 text-xs uppercase tracking-[0.2em] text-white/80">Choose your session <ArrowRight size={12} className="inline" /></div>
            </div>
          </Link>
          <Link to="/house-calls" className="relative overflow-hidden group h-64 flex items-end p-8 border border-white/10" style={{ backgroundImage: "url('https://images.pexels.com/photos/11573495/pexels-photo-11573495.jpeg')", backgroundSize: "cover" }} data-testid="cta-house-calls">
            <div className="absolute inset-0 bg-black/60 group-hover:bg-black/40 transition-colors" />
            <div className="relative">
              <div className="overline text-[#D4AF37]">In-Person</div>
              <h3 className="font-serif text-3xl mt-2">Request a House Call</h3>
              <div className="mt-4 text-xs uppercase tracking-[0.2em] text-white/80">House cleansing & protection <ArrowRight size={12} className="inline" /></div>
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
